import {
  ClipboardList,
  FileText,
  FlaskConical,
  LayoutGrid,
  Package,
  Pill,
  Stethoscope,
  User,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { permissionCheck } from '@/lib/permission-check';

interface SidebarItem {
  value: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  show: boolean;
  description?: string;
}

interface PatientDetailSidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  isGuidedClinic: boolean;
  isHybridClinic: boolean;
  isExternalPatient?: boolean;
  rolePermissions: unknown;
  isPractitioner: boolean;
  isAdmin: boolean;
}

/**
 * Consolidated 5-tab patient sidebar (down from 22 items across 8 sections).
 * Lower-frequency surfaces (Appointments, Tasks, Intakes, Documents, Messaging,
 * Invoices, MDI Cases) live in PatientSecondaryNav docked under the patient
 * header — they're still one click away but visually subordinate.
 */
export default function PatientDetailSidebar({
  activeTab,
  onTabChange,
  isGuidedClinic,
  isHybridClinic,
  isExternalPatient = false,
  rolePermissions,
  isPractitioner,
  isAdmin,
}: PatientDetailSidebarProps) {
  const canViewClinical = isPractitioner || isAdmin;
  const canViewRx = permissionCheck(
    'prescriptions:view',
    rolePermissions,
    canViewClinical,
  );

  const items: SidebarItem[] = [
    {
      value: 'summary',
      label: 'Summary',
      icon: LayoutGrid,
      show: true,
      description: 'AI summary, vitals, demographics',
    },
    {
      value: 'profile',
      label: 'Profile',
      icon: User,
      show: true,
      description: 'Demographics, contacts, insurance',
    },
    {
      value: 'chart',
      label: 'Chart',
      icon: ClipboardList,
      show: !isGuidedClinic && canViewClinical,
      description: 'Problems, allergies, meds, immunizations, vitals',
    },
    {
      value: 'orders-rx',
      label: 'Orders & Rx',
      icon: Pill,
      show: canViewRx,
      description: 'Prescriptions and active medications',
    },
    {
      value: 'labs',
      label: 'Labs',
      icon: FlaskConical,
      show: true,
      description: 'Results, trends, AI insights',
    },
    {
      value: 'notes',
      label: 'Notes',
      icon: FileText,
      show:
        !isGuidedClinic &&
        permissionCheck('clinical_notes:view', rolePermissions, canViewClinical),
      description: 'SOAP notes and timeline',
    },
    // Conditional primary tabs — guided clinics need Guided Orders front and center
    {
      value: 'guided-orders',
      label: 'Guided Orders',
      icon: Package,
      show: isGuidedClinic || isHybridClinic || isExternalPatient,
      description: 'Guided protocol orders',
    },
  ];

  const visible = items.filter((i) => i.show);

  return (
    <aside className='w-56 flex-shrink-0'>
      <nav
        className='sticky top-6 rounded-lg border border-slate-200 bg-white p-2'
        aria-label='Patient sections'
      >
        <ul className='space-y-0.5'>
          {visible.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.value;
            return (
              <li key={item.value}>
                <button
                  type='button'
                  onClick={() => onTabChange(item.value)}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'group flex w-full flex-col items-start gap-0.5 rounded-md px-3 py-2 text-left transition-colors',
                    isActive
                      ? 'bg-teal-50 text-teal-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
                  )}
                >
                  <span className='flex items-center gap-2 text-sm font-medium'>
                    <Icon className='h-4 w-4' />
                    {item.label}
                  </span>
                  {item.description && (
                    <span
                      className={cn(
                        'pl-6 text-[11px]',
                        isActive
                          ? 'text-teal-600/80'
                          : 'text-slate-400 group-hover:text-slate-500',
                      )}
                    >
                      {item.description}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}
